import { AddIncidentPayload } from '@collector/desktop-feature-incidents-queue-core'
import {
  getCoordinateXForTeamSide,
  getParticipantTeamSide,
  isDangerousAttackForCoordinateX,
} from '@collector/shared-ui-domain'
import {
  FreeKickAttribute,
  MissedPenaltyAttribute,
  PenaltySavedByGoalkeeperAttribute,
  SoccerAttributeId,
  SoccerEventStatusId,
  SoccerIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  IncidentQueueCommands,
  IncidentsQueuePayloadConfig,
} from '../../configuration'

const addIncidentWithBigChanceMissedAttribute =
  (attributeId: SoccerAttributeId) =>
  (payload: AddIncidentPayload, queue: IncidentQueueCommands) => {
    queue.addIncident({
      ...payload,
      attribute_ids:
        payload.status_id !== SoccerEventStatusId.PenaltyShootout
          ? [attributeId]
          : payload.attribute_ids,
    })
  }

export const incidentsQueuePayloadConfig: IncidentsQueuePayloadConfig<SoccerIncidentId> =
  {
    [SoccerIncidentId.Foul]: {
      addIncident: (payload, queue, { oppositeParticipant }) => {
        queue.addIncident({
          ...payload,
          incident_id: SoccerIncidentId.Foul,
        })

        queue.addIncident({
          ...payload,
          participant_team_id: oppositeParticipant?.id,
          incident_id: SoccerIncidentId.FreeKick,
        })
      },
    },
    [SoccerIncidentId.FreeKick]: {
      addIncident: (payload, queue, { oppositeParticipant }) => {
        queue.addIncident({
          ...payload,
          incident_id: SoccerIncidentId.Foul,
        })

        queue.addIncident({
          ...payload,
          participant_team_id: oppositeParticipant?.id,
          incident_id: SoccerIncidentId.FreeKick,
        })
      },
      updateIncident: (payload, id, queue, { participant }) => {
        if (payload.x_pos !== undefined && payload.y_pos !== undefined) {
          const isDangerousAttack = isDangerousAttackForCoordinateX(
            getCoordinateXForTeamSide(
              payload.x_pos,
              getParticipantTeamSide(participant),
            ),
          )

          if (isDangerousAttack) {
            payload.attribute_ids = [FreeKickAttribute.DangerousFreeKick]
          }
        }

        queue.updateIncident(payload, id)
      },
    },
    [SoccerIncidentId.MissedPenalty]: {
      addIncident: addIncidentWithBigChanceMissedAttribute(
        MissedPenaltyAttribute.BigChanceMissed,
      ),
    },
    [SoccerIncidentId.PenaltySavedByGoalkeeper]: {
      addIncident: addIncidentWithBigChanceMissedAttribute(
        PenaltySavedByGoalkeeperAttribute.BigChanceMissed,
      ),
    },
    [SoccerIncidentId.Offside]: {
      addIncident: (payload, queue, { oppositeParticipant }) => {
        queue.addIncident({
          ...payload,
          incident_id: SoccerIncidentId.Offside,
        })
        queue.addIncident({
          ...payload,
          participant_team_id: oppositeParticipant?.id,
          incident_id: SoccerIncidentId.FreeKick,
        })
      },
    },
    [SoccerIncidentId.SubstitutionIn]: {
      addIncident: (payload, queue) => {
        const substitutionOutUuid = queue.addIncident({
          ...payload,
          incident_id: SoccerIncidentId.SubstitutionOut,
        })
        queue.addIncident(
          {
            ...payload,
            incident_id: SoccerIncidentId.SubstitutionIn,
          },
          substitutionOutUuid,
        )
      },
    },
    [SoccerIncidentId.Penalty]: {
      addIncident: (payload, queue, { participant, oppositeParticipant }) => {
        const { confirmation, ...foulPayload } = payload
        const penaltyPosition =
          getParticipantTeamSide(participant!) === 'home'
            ? { x: 90, y: 50 }
            : { x: 10, y: 50 }

        queue.addIncident({
          ...foulPayload,
          participant_team_id: oppositeParticipant?.id,
          incident_id: SoccerIncidentId.Foul,
        })

        return queue.addIncident({
          ...payload,
          participant_team_id: participant?.id,
          incident_id: SoccerIncidentId.Penalty,
          x_pos: penaltyPosition.x,
          y_pos: penaltyPosition.y,
        })
      },
    },
  }
